// Pure filter-state logic for the Merlin Visibility Pane (no React).
//
// The page keeps its filter / sort / page state in the URL so a view is
// shareable ("here's every failed Quality Co-Pilot thread this week") and
// survives a reload. This module is the single translation between that URL
// and the typed state, plus the small state transitions the controls apply
// (toggle a multi-select value, flip a sort, page). Every branch is unit-tested.

import type {
  ChannelKind,
  FeedbackFilter,
  MonitorPageParams,
  MonitorQueryParams,
  MonitorSortKey,
  SortDirection,
  StreamKind,
} from '../../services/merlinMonitor';
import type { ConversationState } from '../../services/agentConsole';

export interface MonitorFilterState {
  q: string;
  streams: StreamKind[];
  profileIds: string[];
  states: ConversationState[];
  models: string[];
  ownerIds: string[];
  channels: ChannelKind[];
  /** ISO date (yyyy-mm-dd) as typed into the date inputs; '' = unbounded. */
  since: string;
  until: string;
  feedback: FeedbackFilter | '';
  includeArchived: boolean;
  sortBy: MonitorSortKey;
  sortDir: SortDirection;
  page: number; // 0-based
  pageSize: number;
}

export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZES = [25, 50, 100, 200];

export const DEFAULT_FILTERS: MonitorFilterState = {
  q: '',
  streams: [],
  profileIds: [],
  states: [],
  models: [],
  ownerIds: [],
  channels: [],
  since: '',
  until: '',
  feedback: '',
  includeArchived: true,
  sortBy: 'last_activity',
  sortDir: 'desc',
  page: 0,
  pageSize: DEFAULT_PAGE_SIZE,
};

const SORT_KEYS: MonitorSortKey[] = [
  'last_activity',
  'created',
  'title',
  'state',
  'model',
  'profile',
  'owner',
  'messages',
  'tool_calls',
  'cost',
  'tokens',
  'feedback_up',
  'feedback_down',
];

const FEEDBACK_VALUES: FeedbackFilter[] = ['any', 'up', 'down', 'none'];

/** Text columns sort ascending on first click; numbers / dates descending. */
export function defaultDirectionFor(key: MonitorSortKey): SortDirection {
  switch (key) {
    case 'title':
    case 'state':
    case 'model':
    case 'profile':
    case 'owner':
      return 'asc';
    default:
      return 'desc';
  }
}

/** Click a column header: same column flips direction, a new one takes its
 *  natural direction. Always returns to page 0. */
export function toggleSort(state: MonitorFilterState, key: MonitorSortKey): MonitorFilterState {
  if (state.sortBy === key) {
    return { ...state, sortDir: state.sortDir === 'asc' ? 'desc' : 'asc', page: 0 };
  }
  return { ...state, sortBy: key, sortDir: defaultDirectionFor(key), page: 0 };
}

/** Add/remove one value in a multi-select list (order preserved), page reset. */
export function toggleListValue<T extends string>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function countActiveFilters(state: MonitorFilterState): number {
  let n = 0;
  if (state.q.trim()) n += 1;
  n += state.streams.length ? 1 : 0;
  n += state.profileIds.length ? 1 : 0;
  n += state.states.length ? 1 : 0;
  n += state.models.length ? 1 : 0;
  n += state.ownerIds.length ? 1 : 0;
  n += state.channels.length ? 1 : 0;
  n += state.since || state.until ? 1 : 0;
  n += state.feedback ? 1 : 0;
  n += state.includeArchived ? 0 : 1;
  return n;
}

// ---------------------------------------------------------------------------
// URL ↔ state
// ---------------------------------------------------------------------------

function list(params: URLSearchParams, key: string): string[] {
  return params.getAll(key).filter((v) => v !== '');
}

function isSortKey(v: string | null): v is MonitorSortKey {
  return v !== null && (SORT_KEYS as string[]).includes(v);
}

export function filtersFromSearchParams(params: URLSearchParams): MonitorFilterState {
  const sortBy = params.get('sort');
  const dir = params.get('dir');
  const feedback = params.get('feedback');
  const page = Number.parseInt(params.get('page') ?? '', 10);
  const size = Number.parseInt(params.get('size') ?? '', 10);
  return {
    q: params.get('q') ?? '',
    streams: list(params, 'stream') as StreamKind[],
    profileIds: list(params, 'profile'),
    states: list(params, 'state') as ConversationState[],
    models: list(params, 'model'),
    ownerIds: list(params, 'owner'),
    channels: list(params, 'channel') as ChannelKind[],
    since: params.get('since') ?? '',
    until: params.get('until') ?? '',
    feedback: feedback && (FEEDBACK_VALUES as string[]).includes(feedback)
      ? (feedback as FeedbackFilter)
      : '',
    includeArchived: params.get('archived') !== '0',
    sortBy: isSortKey(sortBy) ? sortBy : DEFAULT_FILTERS.sortBy,
    sortDir: dir === 'asc' || dir === 'desc' ? dir : DEFAULT_FILTERS.sortDir,
    page: Number.isFinite(page) && page > 0 ? page : 0,
    pageSize: PAGE_SIZES.includes(size) ? size : DEFAULT_PAGE_SIZE,
  };
}

/** Only non-default fields are written, so the bare route stays `/merlin-monitor`. */
export function filtersToSearchParams(state: MonitorFilterState): URLSearchParams {
  const out = new URLSearchParams();
  if (state.q.trim()) out.set('q', state.q.trim());
  for (const s of state.streams) out.append('stream', s);
  for (const p of state.profileIds) out.append('profile', p);
  for (const s of state.states) out.append('state', s);
  for (const m of state.models) out.append('model', m);
  for (const o of state.ownerIds) out.append('owner', o);
  for (const c of state.channels) out.append('channel', c);
  if (state.since) out.set('since', state.since);
  if (state.until) out.set('until', state.until);
  if (state.feedback) out.set('feedback', state.feedback);
  if (!state.includeArchived) out.set('archived', '0');
  if (state.sortBy !== DEFAULT_FILTERS.sortBy) out.set('sort', state.sortBy);
  if (state.sortDir !== DEFAULT_FILTERS.sortDir) out.set('dir', state.sortDir);
  if (state.page > 0) out.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) out.set('size', String(state.pageSize));
  return out;
}

// ---------------------------------------------------------------------------
// state → API params
// ---------------------------------------------------------------------------

/** A yyyy-mm-dd "since" is the start of that local day; "until" is the START
 *  of the day AFTER (the backend's until is exclusive), so a single-day range
 *  since=until=today covers today. Returns undefined for '' / unparseable. */
export function dayBoundIso(day: string, edge: 'start' | 'end'): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const [y, m, d] = day.split('-').map((n) => Number.parseInt(n, 10));
  const date = new Date(y, m - 1, d + (edge === 'end' ? 1 : 0), 0, 0, 0, 0);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function toPageParams(state: MonitorFilterState): MonitorPageParams {
  return {
    q: state.q.trim() || undefined,
    stream: state.streams.length ? state.streams : undefined,
    profile_id: state.profileIds.length ? state.profileIds : undefined,
    state: state.states.length ? state.states : undefined,
    model: state.models.length ? state.models : undefined,
    owner_id: state.ownerIds.length ? state.ownerIds : undefined,
    channel: state.channels.length ? state.channels : undefined,
    since: dayBoundIso(state.since, 'start'),
    until: dayBoundIso(state.until, 'end'),
    feedback: state.feedback || undefined,
    include_archived: state.includeArchived ? undefined : false,
    sort_by: state.sortBy,
    sort_dir: state.sortDir,
    limit: state.pageSize,
    offset: state.page * state.pageSize,
  };
}

/** The summary shares the filters but not the sort / page. */
export function toSummaryParams(state: MonitorFilterState): MonitorQueryParams {
  const page = toPageParams(state);
  return {
    q: page.q,
    stream: page.stream,
    profile_id: page.profile_id,
    state: page.state,
    model: page.model,
    owner_id: page.owner_id,
    channel: page.channel,
    since: page.since,
    until: page.until,
    feedback: page.feedback,
    include_archived: page.include_archived,
  };
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}
